import React, {  useLayoutEffect } from 'react';
import "tabs-pattern/dist/foundation.css";
import { tabs as tabsInit } from 'tabs-pattern/src/tabs-ds2';
import type { TabData } from '../../config/tabsConfig';

// ✅ Declare pljs on Window type
declare global {
  interface Window {
    pljs: {
      getCurrentLang: (el?: HTMLElement) => string;
      createElementFromHTML?: (html: string) => HTMLElement;
      createInnerWrap?: (el: HTMLElement, tag: string, attr: string, value: string) => void;
      getWindowWidth?: () => number;
      getElCount?: (els: NodeListOf<Element>) => number;
    };
  }
}

interface TabsProps {
  tabs: TabData[];
  activeTabId?: number;
  extraProps?: Record<string, any>;
}

const Tabs: React.FC<TabsProps> = ({ tabs: tabsList, activeTabId, extraProps }) => {
  useLayoutEffect(() => {
    try {
      tabsInit();
      console.log(' Tabs initialized successfully');
    } catch (error) {
      console.error(' Tabs init error:', error);
    }
  }, [tabsList.length]);

  // Set active tab after initialization
  useLayoutEffect(() => {
    if (activeTabId !== undefined) {
      const tabIndex = tabsList.findIndex(tab => tab.id === activeTabId);
      if (tabIndex !== -1) {
        const container = document.querySelector('.ds-tabs');
        if (!container) return;
        const tabLinks = container.querySelectorAll('.ds-tabs__link');
        const tabPanels = container.querySelectorAll('.ds-tabs__panel');

        // Check if the desired tab is already active to avoid unnecessary work
        if (tabLinks[tabIndex]?.classList.contains('-active')) return;
        
        // Remove active state from all tabs
        tabLinks.forEach(link => {
          link.classList.remove('-active');
          link.setAttribute('aria-selected', 'false');
          link.setAttribute('tabindex', '-1');
        });
        tabPanels.forEach(panel => {
          panel.classList.remove('-active');
          panel.setAttribute('aria-hidden', 'true');
        });
        
        // Add active state to selected tab and its panel
        if (tabLinks[tabIndex] && tabPanels[tabIndex]) {
          tabLinks[tabIndex].classList.add('-active');
          tabLinks[tabIndex].setAttribute('aria-selected', 'true');
          tabLinks[tabIndex].setAttribute('tabindex', '0');
          tabPanels[tabIndex].classList.add('-active');
          tabPanels[tabIndex].setAttribute('aria-hidden', 'false');
        }
      }
    }
  }, [activeTabId, tabsList]);

  return (
    <div
      className={`ds-tabs -overflow-button grid-container pl-0!`} 
      data-ds-theme="vessel"
      data-ds-variant="basic"
      data-ds-version="2"
      data-pl-variation="overflow-button"
      data-pl-version="2"
      data-panel-announcement-start=""
      data-panel-announcement-end=""
    >
      <div className="ds-tabs__nav-container">
        <div className="ds-tabs__nav">
          <ul className="ds-tabs__list">
            {tabsList.map((tab, index) => {
              const isActive = activeTabId === tab.id || (activeTabId === undefined && index === 0);
              return (
                <li key={tab.id} className="ds-tabs__list-item">
                  <button 
                    type="button"
                    role="tab"
                    className={`ds-tabs__link${isActive ? ' -active' : ''}`}
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                  >
                    <span className="ds-tabs__text">{tab.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="ds-tabs__nav-more-button">
          <button className="more-button__trigger" aria-haspopup="true" tabIndex={0}>
            <span className="screenreader-only">show all</span>
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <use href="assets/icons/ds2/morehorizontal.svg#more-horizontal"></use>
            </svg>
          </button>
          <ul className="more-button__list"></ul>
        </div>
      </div>
      <div className="ds-tabs__content">
        {tabsList.map((tab, index) => {
          const Component = tab.component;
          const isActive = activeTabId === tab.id || (activeTabId === undefined && index === 0);
          return (
            <div 
              key={tab.id} 
              className={`ds-tabs__panel${isActive ? ' -active' : ''}`}
              aria-hidden={!isActive}
            >
              <Component {...(extraProps || {})} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Tabs;
